import type {
  BannerStyleMap,
  HeaderItem,
  HighlightProps,
  OrderBookCellProps,
  OrderBookContainerProps,
  OrderBookTableContainerProps,
} from './types'

import { forwardRef, type HTMLAttributes } from 'react'

import Icon, { IconName } from '@/components/atomic/icon'
import {
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atomic/table-basic-legacy'
import { BodySubtext } from '@/components/atomic/text'
import Tooltip from '@/components/atomic/tooltip'
import { cn } from '@/utils/shadcn/utils'

import styles from './order-book.module.css'
import { calculateMaxHeight } from './utils'

// =========================================
// Style Constants
// =========================================
// Helper functions to get banner styles
export const getBannerStyles = () => {
  // Bids: red, Asks: green
  return {
    background: {
      asksActive: cn(
        `bg-moment-green-200 dark:bg-moment-green-1000 ${
          // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
          styles['green']
        }`,
        'hover:bg-moment-green-300 dark:hover:bg-moment-green-900',
      ),
      bidsActive: cn(
        `bg-moment-red-200 dark:bg-moment-red-900 ${
          // biome-ignore lint/complexity/useLiteralKeys: Bulk suppression as part of biome migration
          styles['red']
        }`,
        'hover:bg-moment-red-300 dark:hover:bg-moment-red-800',
      ),
      neutral: cn(
        'bg-moment-blue-grey-200 dark:bg-moment-blue-grey-1000',
        'hover:bg-moment-blue-grey-300 dark:hover:bg-moment-blue-grey-900',
      ),
    },
    text: {
      asksActive: 'text-moment-green-1000 dark:text-moment-green-600',
      bidsActive: 'text-moment-red-900 dark:text-moment-red-300',
      neutral: '',
    },
  }
}

const bannerBackground: BannerStyleMap = getBannerStyles().background
const bannerText = getBannerStyles().text

// =========================================
// Container Components
// =========================================
export const OrderBookContainer: React.FC<OrderBookContainerProps> = ({
  children,
  hasBorder = false,
  className = '',
}) => {
  return (
    <div
      className={cn(
        'flex',
        'flex-col',
        'grow',
        'h-fit',
        'max-h-full',
        'relative',
        'overflow-y-hidden',
        hasBorder && 'rounded-[6px] border-1 border-primary-border',
        className,
      )}
    >
      {children}
    </div>
  )
}

export const OrderBookTableContainer = forwardRef<
  HTMLDivElement,
  OrderBookTableContainerProps
>(
  (
    {
      children,
      showBoth,
      rows,
      className = '',
      isCollapsed,
      showPreAndPostFeeValues = false,
    },
    ref,
  ) => {
    // Calculate max height based on actual content vs specified rows
    const maxHeightStyle =
      rows !== undefined
        ? {
            maxHeight: `${calculateMaxHeight(rows, showPreAndPostFeeValues)}px`,
          }
        : {}

    return (
      <div
        className={cn(
          'scrollbar-hide overflow-y-auto transition-all duration-450 ease',
          // Use CSS Grid/Flexbox for better content fitting
          'flex flex-col',
          isCollapsed ? 'delay-225 h-0' : 'delay-0 h-fit',
          className,
        )}
        style={maxHeightStyle}
        ref={ref}
      >
        <div
          className={cn(
            'flex flex-grow w-full',
            'bg-primary-border',
            showBoth && 'space-x-[1px]',
            // Ensure content doesn't create extra space
            'min-h-0', // Important: prevents flex items from growing beyond content
          )}
        >
          {children}
        </div>
      </div>
    )
  },
)
OrderBookTableContainer.displayName = 'OrderBookTableContainer'

export const SummaryBannerContainer: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  return (
    <div className="p-0.75 flex flex-row moment-primary-100 dark:moment-primary-100 bg-tertiary-background border-b-1 border-primary-border">
      {children}
    </div>
  )
}

// =========================================
// Summary Components
// =========================================
export const SummaryMidContainer = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Trigger the click handler when Enter or Space is pressed
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      props.onClick?.(event as unknown as React.MouseEvent<HTMLDivElement>)
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Bulk suppression as part of biome migration
    <div
      ref={ref}
      className={cn(
        'flex',
        'h-[24px]',
        'justify-center items-center',
        'px-[5px] pt-[2px] pb-[1px]',
        'cursor-pointer',
        bannerBackground.neutral,
        bannerText.neutral,
        className,
      )}
      onClick={props.onClick}
      onKeyDown={handleKeyDown}
      role={props.role || 'button'}
      tabIndex={props.tabIndex || 0}
    >
      {children}
    </div>
  )
})
SummaryMidContainer.displayName = 'SummaryMidContainer'

export const SummarySideBannerButtonContainer = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ children, className, ...props }, ref) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Trigger the click handler when Enter or Space is pressed
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      props.onClick?.(event as unknown as React.MouseEvent<HTMLDivElement>)
    }
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Bulk suppression as part of biome migration
    <div
      ref={ref}
      className={cn(
        'h-[24px]',
        'flex grow w-full',
        'items-center justify-between',
        'px-[5px] pt-[2px] pb-[1px]',
        'cursor-pointer',
        className,
      )}
      onClick={props.onClick}
      onKeyDown={handleKeyDown}
      role={props.role || 'button'}
      tabIndex={props.tabIndex || 0}
    >
      {children}
    </div>
  )
})
SummarySideBannerButtonContainer.displayName =
  'SummarySideBannerButtonContainer'

// =========================================
// Table Components
// =========================================
/**
 * OrderBookTableHeader component
 * Reusable header for both Bids and Asks tables
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isAsks - If true, displays header for Asks table; if false, for Bids table
 * @returns {JSX.Element} Table header row with appropriate column headers
 */
export const OrderBookTableHeader = ({
  isAsks = false,
  showActionColumn = false,
  showVenuesColumn = false,
  showSpreadToBenchmark = false,
  showSource = false,
  showYieldToWorst = false,
  showMbrAndIncrement = false,
  benchmarkIsin,
  isActivePanelBoth,
}: {
  isAsks: boolean
  showActionColumn: boolean
  showVenuesColumn: boolean
  showSpreadToBenchmark: boolean
  showSource: boolean
  showYieldToWorst: boolean
  showMbrAndIncrement?: boolean
  benchmarkIsin?: string | null
  isActivePanelBoth: boolean
}) => {
  // Header for bids table and bids table is only table shown (i.e., not both)
  const showBidActionColumnOnRight = !isAsks && !isActivePanelBoth
  const headerItems: HeaderItem[] = [
    // Show action column on right side if only bid panel is active
    // (this works because we reverse the array for bids)
    ...(showActionColumn && showBidActionColumnOnRight
      ? [
          {
            label: '',
            className: cn(styles['col-action'], 'w-[80px]'),
          },
        ]
      : []),
    { label: 'Price', className: styles['col-price'] },
    ...(showYieldToWorst
      ? [{ label: 'YTW', className: styles['col-ytw'] }]
      : []),
    {
      label: 'Qty',
      className: styles['col-qty'],
    },
    {
      label: 'Min',
      className: styles['col-min'],
    },
    ...(showSpreadToBenchmark
      ? [
          {
            label: 'Spread',
            className: styles['col-spread'],
          },
        ]
      : []),
    ...(showVenuesColumn
      ? [
          {
            label: 'Venue',
            className: styles['col-venue'],
          },
        ]
      : []),
    ...(showSource
      ? [
          {
            label: 'Source',
            className: styles['col-source'],
          },
        ]
      : []),
    ...(showActionColumn && !showBidActionColumnOnRight
      ? [
          {
            label: '',
            className: cn(styles['col-action'], 'w-[80px]'),
          },
        ]
      : []),
  ]

  // For bids (not asks), reverse the array
  const orderedItems = isAsks ? headerItems : [...headerItems].reverse()

  return (
    <TableHeader className="z-10 bg-tertiary-background">
      <TableRow>
        {orderedItems.map((item, index) => (
          <TableHead
            key={index}
            className={cn(
              isAsks ? 'text-left' : 'text-right',
              'border-b border-primary-border',
              item.className,
            )}
          >
            <div
              className={cn(
                'flex items-center gap-[2px]',
                isAsks ? 'justify-start' : 'justify-end',
              )}
            >
              {item.label !== 'Spread' &&
                item.label !== 'Qty' &&
                item.label !== 'Min' &&
                item.label}
              {item.label === 'Spread' && benchmarkIsin && (
                <>
                  {isAsks && item.label}
                  <Tooltip
                    simpleText={`Spread is shown in basis points and is with respect to the benchmark ISIN ${benchmarkIsin}`}
                    className={cn(
                      'cursor-pointer text-left max-w-[200px] whitespace-normal',
                      isAsks ? 'translate-x-[-50px]' : 'translate-x-[100px]',
                    )}
                    side={'top'}
                  >
                    <div className="pointer-events-auto">
                      <Icon
                        iconName={IconName.InformationCircleIcon}
                        className="w-3 h-3 ml-[2px]"
                        outline
                      />
                    </div>
                  </Tooltip>
                  {!isAsks && item.label}
                </>
              )}
              {item.label === 'Qty' && (
                <>
                  {isAsks && item.label}
                  {showMbrAndIncrement && (
                    <Tooltip
                      simpleText="Number of bonds available at this price. MBR (Minimum Balance Remaining) - if provided by the dealer, the minimum quantity that must remain after a trade."
                      className={cn(
                        'cursor-pointer text-left max-w-[200px] whitespace-normal',
                        isAsks ? 'translate-x-[-50px]' : 'translate-x-[100px]',
                      )}
                      side={'top'}
                    >
                      <div className="pointer-events-auto">
                        <Icon
                          iconName={IconName.InformationCircleIcon}
                          className="w-3 h-3 ml-[2px]"
                          outline
                        />
                      </div>
                    </Tooltip>
                  )}
                  {!isAsks && item.label}
                </>
              )}
              {item.label === 'Min' && (
                <>
                  {isAsks && item.label}
                  {showMbrAndIncrement && (
                    <Tooltip
                      simpleText="Minimum number of bonds required to trade. Inc (Minimum Increment) - if provided by the dealer, the minimum quantity increment for partial fills."
                      className={cn(
                        'cursor-pointer text-left max-w-[200px] whitespace-normal',
                        isAsks ? 'translate-x-[-50px]' : 'translate-x-[100px]',
                      )}
                      side={'top'}
                    >
                      <div className="pointer-events-auto">
                        <Icon
                          iconName={IconName.InformationCircleIcon}
                          className="w-3 h-3 ml-[2px]"
                          outline
                        />
                      </div>
                    </Tooltip>
                  )}
                  {!isAsks && item.label}
                </>
              )}
            </div>
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}

// =========================================
// Dynamic Highlight Components
// =========================================
interface DynamicHighlightProps extends HighlightProps {
  height?: number
}

// Helper functions to get the correct colors
const getBidColors = () => {
  return {
    bg: 'bg-moment-red-200 dark:bg-moment-red-900',
    border: 'border-moment-red-800 dark:border-moment-red-600',
  }
}

const getAskColors = () => {
  return {
    bg: 'bg-moment-green-200 dark:bg-moment-green-1000',
    border: 'border-moment-green-800 dark:border-moment-green-800',
  }
}

export const HighlightBidRow: React.FC<DynamicHighlightProps> = ({
  children,
  className,
}) => {
  const colors = getBidColors()

  return (
    <TableRow
      className={cn(
        colors.bg,
        'text-primary',
        'border-2',
        colors.border,
        'sticky top-8.5',
        'z-10',
        className,
      )}
    >
      {children}
    </TableRow>
  )
}

export const HighlightBidCell: React.FC<DynamicHighlightProps> = ({
  children,
  className,
  height,
}) => {
  const colors = getBidColors()

  return (
    <TableCell
      className={cn(
        'text-center',
        'border-y',
        colors.border,
        'first:border-l first:rounded-l',
        'last:border-r last:rounded-r',
        className,
      )}
      height={height}
    >
      {children}
    </TableCell>
  )
}

export const HighlightAskRow: React.FC<DynamicHighlightProps> = ({
  children,
  className,
}) => {
  const colors = getAskColors()

  return (
    <TableRow
      className={cn(
        colors.bg,
        'text-primary',
        'border-2',
        colors.border,
        'sticky top-8.5',
        'z-10',
        className,
      )}
    >
      {children}
    </TableRow>
  )
}

export const HighlightAskCell: React.FC<DynamicHighlightProps> = ({
  children,
  className,
  height,
}) => {
  const colors = getAskColors()

  return (
    <TableCell
      className={cn(
        'text-center',
        'border-y',
        colors.border,
        'first:border-l first:rounded-l',
        'last:border-r last:rounded-r',
        className,
      )}
      height={height}
    >
      {children}
    </TableCell>
  )
}

// Using the same pattern as HighlightBidRow and HighlightAskRow
export const DisabledRow: React.FC<HighlightProps> = ({
  children,
  className,
}) => {
  return <TableRow className={cn(className)}>{children}</TableRow>
}

export const DisabledCell: React.FC<OrderBookCellProps> = ({
  children,
  className,
  hasBorder = true,
  height,
}) => {
  return (
    <TableCell
      className={cn(
        'text-center',
        'bg-moment-blue-grey-100 dark:bg-moment-blue-grey-1000',
        'text-moment-blue-grey-600 dark:text-moment-blue-grey-600',
        hasBorder && 'border-b border-primary-border',
        className,
      )}
      height={height}
    >
      {children}
    </TableCell>
  )
}

export const OrderBookCell: React.FC<OrderBookCellProps> = ({
  children,
  className,
  hasBorder = true,
  height,
}) => {
  return (
    <TableCell
      className={cn(
        'bg-tertiary-background',
        'text-center',
        hasBorder && 'border-b border-primary-border',
        className,
      )}
      height={height}
    >
      {children}
    </TableCell>
  )
}

// =========================================
// Order Book Title Component
// =========================================
export const OrderBookTitle: React.FC<{
  titleText: string
  secondaryText?: string
}> = ({ titleText, secondaryText }) => {
  return (
    <div className="w-full p-3 flex justify-between items-center border-b border-primary-border">
      <BodySubtext>{titleText}</BodySubtext>
      {secondaryText && (
        <BodySubtext className="text-secondary">{secondaryText}</BodySubtext>
      )}
    </div>
  )
}
